import { useCallback } from 'react';
import { useAuthStore } from './auth.store';
import { AuthApi } from '../../services/auth.service';
import { bindDicomAccessToken } from '../dicom-viewer/init/requestHeaders';
import {
  bindApiAccessToken,
  bindApiAuthFailureHandler,
  bindApiRefreshHandler,
} from '../../services/api';

// 模块级 bootstrap 去重状态（详见 useAuthActions().bootstrap 注释）。
// 整页 reload 会让模块重新加载、状态自动重置，所以不会卡住合法的"用户主动刷"路径。
let bootstrapInflight: Promise<void> | null = null;
let bootstrapCompleted = false;

/**
 * 把 auth.store 注入到 services/api.ts，并导出登录 / 登出 / 刷新 / bootstrap 等动作。
 *
 * 必须在 RootProviders mount 时调用 bootstrap() 一次（首次自检 refresh 是否仍有效）。
 */
export function bindAuthToApiClient(): void {
  bindDicomAccessToken(() => useAuthStore.getState().accessToken);
  bindApiAccessToken(() => useAuthStore.getState().accessToken);
  bindApiRefreshHandler(async () => {
    try {
      const res = await AuthApi.refresh();
      useAuthStore.getState().setAuthenticated({
        user: res.user,
        accessToken: res.accessToken,
        accessTokenExpiresAt: res.accessTokenExpiresAt,
      });
      return true;
    } catch {
      useAuthStore.getState().setUnauthenticated();
      return false;
    }
  });
  bindApiAuthFailureHandler(() => {
    useAuthStore.getState().setUnauthenticated();
  });
}

export function useAuthActions() {
  const setAuthenticated = useAuthStore((s) => s.setAuthenticated);
  const setUnauthenticated = useAuthStore((s) => s.setUnauthenticated);

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await AuthApi.login({ email, password });
      setAuthenticated({
        user: res.user,
        accessToken: res.accessToken,
        accessTokenExpiresAt: res.accessTokenExpiresAt,
      });
    },
    [setAuthenticated],
  );

  const logout = useCallback(async () => {
    try {
      await AuthApi.logout();
    } catch {
      // 即便调失败，本地也要登出
    }
    setUnauthenticated();
  }, [setUnauthenticated]);

  const bootstrap = useCallback(async () => {
    // 首次进入 / 刷新页面：尝试用 refresh cookie 还原登录态。
    // 失败（包括没 cookie）就标 unauthenticated，由 AuthGuard 跳 /login。
    //
    // **必须去重**：React StrictMode 在 dev 模式下故意把 useEffect 跑两遍
    // （mount → unmount → re-mount），会让 bootstrap 并发跑两次。两次 refresh
    // 都带同一个 hash_old，server 处理完第一个后会把 hash_old 写入
    // previousRefreshTokenHash；第二个再到时被 ADR-0010 reuse_detected 撤销
    // session，导致刚刚还登录的用户被踢回 /login。
    //
    // 模块级 inflight + completed 两个标记：
    //  - inflight：同一次页面生命周期内的并发调用复用同一个 Promise（防 StrictMode）
    //  - completed：完成后再次调用直接 no-op（防其它路径手工调 bootstrap）
    //  - 整页刷新会重置模块状态，所以"用户主动刷页面"仍能正常触发新 refresh
    if (bootstrapCompleted) return;
    if (bootstrapInflight) return bootstrapInflight;
    bootstrapInflight = (async () => {
      try {
        const res = await AuthApi.refresh();
        setAuthenticated({
          user: res.user,
          accessToken: res.accessToken,
          accessTokenExpiresAt: res.accessTokenExpiresAt,
        });
      } catch {
        setUnauthenticated();
      } finally {
        bootstrapCompleted = true;
        bootstrapInflight = null;
      }
    })();
    return bootstrapInflight;
  }, [setAuthenticated, setUnauthenticated]);

  return { login, logout, bootstrap };
}
